import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { steps } from "../const/job-steps.const";
import { componentMap } from "../const/component-steps.map.const";

export function useJobStepperNavigation(events, initialEventUid) {
  const [eventIndex, setEventIndex] = useState(0);
  const [manualStep, setManualStep] = useState(null);

  const initialUidRef = useRef(initialEventUid);

  const resetToFirstEvent = () => {
    setEventIndex(0);
  };

  useEffect(() => {
    if (!events || events.length === 0) return;

    const idx = events.findIndex((e) => e.uid === initialUidRef.current);

    if (idx !== -1) {
      setEventIndex(idx);
    } else {
      setEventIndex(0);
    }
  }, [events]);

  const goToEvent = (uid) => {
    const index = events.findIndex((e) => e.uid === uid);
    if (index >= 0) setEventIndex(index);
  };

  const currentEvent = events?.[eventIndex] ?? null;

  const realStepIndex = steps.findIndex((s) => s.value === currentEvent?.status);

  const scheduledIndex = steps.findIndex((s) => s.value === "scheduled");
  const inProgressIndex = steps.findIndex((s) => s.value === "inProgress");

  const maxAccessibleStepIndex = useMemo(() => {
    if (!currentEvent) return realStepIndex;

    if (currentEvent.status === "scheduled") {
      return inProgressIndex;
    }

    return realStepIndex;
  }, [currentEvent, realStepIndex, inProgressIndex]);

  const displayStepIndex = manualStep !== null ? manualStep : realStepIndex;

  useEffect(() => {
    setManualStep(null);
  }, [eventIndex]);

  const nextEvent = () => setEventIndex((i) => (i < events.length - 1 ? i + 1 : i));

  const prevEvent = () => setEventIndex((i) => (i > 0 ? i - 1 : i));

  const handleStepClick = useCallback((index) => {
    if (index <= maxAccessibleStepIndex) {
      setManualStep(index);
    }
  }, [maxAccessibleStepIndex]);

  const selectedStatus = steps[displayStepIndex]?.value;

  const CurrentComponent = useMemo(() => componentMap[selectedStatus] || null, [selectedStatus]);

  return {
    eventIndex,
    currentEvent,
    setEventIndex,
    displayStepIndex,
    realStepIndex,
    selectedStatus,
    CurrentComponent,
    handleStepClick,
    nextEvent,
    prevEvent,
    goToEvent,
    resetToFirstEvent,

    scheduledIndex,
    inProgressIndex,
    maxAccessibleStepIndex,
  };
}
